import { createContext } from "react";
import type { BaseCreatorDto, TrackMinDto } from "@/lib/lutely-api/gen/dtos";
import type {
  TracksControllerTrackCreateType,
  TracksControllerTracksListType,
} from "@/lib/lutely-api/gen/dtos";

export type ProcessingTrackStatus = "pending" | "generating" | "done";

export interface ProcessingTrack extends TrackMinDto {
  completionPercentage: number;
  status: ProcessingTrackStatus;
}

export type UploadQueueItemStatus =
  | "pending"
  | "uploading"
  | "creating"
  | "done"
  | "error";

/** One file of an upload batch, from selection through to its created track. */
export interface UploadQueueItem {
  id: string;
  file: File;
  /** S3 upload progress, 0–100. */
  progress: number;
  status: UploadQueueItemStatus;
  /** Set only when status is "error". */
  errorMessage?: string;
}

export interface TrackUploadContextFields {
  /** True while any file of the current batch is still in flight. */
  isUploading: boolean;
  /** Selection-level problem (unsupported files, batch/quota cap). */
  errorMessage?: string;
  /** The current batch, in the order the files were picked. */
  uploadQueue: UploadQueueItem[];
  processingTracks: ProcessingTrack[];
  removeTrack: (id: string) => void;
  clearFinished: () => void;
  setShowUpload: (visible: boolean) => void;
  /**
   * Opens the wizard pinned to one artist, for flows where the target is
   * already decided — an album belongs to a creator, so switching artist
   * mid-upload could only produce a track the album will reject.
   * Cleared when the wizard closes.
   */
  openUploadForCreator: (creatorId: string) => void;
  /**
   * Like openUploadForCreator, plus an optional Master-track preset and a
   * one-shot callback with the created tracks, so a release page can attach
   * the upload to itself (8/25 notes). The callback fires once per batch, with
   * every track that uploaded successfully. Cleared when the wizard closes.
   */
  openUploadForRelease: (
    creatorId: string,
    options?: {
      mastering?: boolean;
      /** Caps the batch, e.g. 1 for a Single. */
      maxFiles?: number;
      onCreated?: (tracks: TrackMinDto[]) => void;
    },
  ) => void;
  /** Set only while the wizard is pinned; null for every ordinary upload. */
  lockedUploadCreatorId: string | null;
  /** Batch cap from the release page; null when the plan decides. */
  uploadMaxFiles: number | null;
  /**
   * Opens the wizard with mastering already chosen, for the Create menu's
   * "Master track" entry: the user said what they wanted by picking it, so the
   * wizard skips the step that asks again. Cleared when the wizard closes.
   */
  openUploadForMastering: () => void;
  /** True while the wizard was entered through "Master track". */
  uploadMasteringPreset: boolean;
  /**
   * Validates a picked batch and returns the files worth uploading. Anything
   * rejected — and anything past `maxFiles` — is reported through
   * `errorMessage` rather than dropped silently.
   */
  handleFilesSelect: (
    files: File[],
    type: TracksControllerTracksListType,
    maxFiles: number,
  ) => File[];
  /** Uploads a batch, a few files at a time, and creates a track for each. */
  createTracks: (
    files: File[],
    type: TracksControllerTrackCreateType,
    creatorId: string,
    shouldMaster: boolean,
  ) => Promise<void>;
}

export interface AudiolabContextType extends TrackUploadContextFields {
  selectedCreator: BaseCreatorDto | undefined;
  setSelectedCreator: (creator: BaseCreatorDto) => void;
  allCreators: BaseCreatorDto[];
  defaultCreator: BaseCreatorDto | undefined;
  isReadOnly: boolean;
  /** Label roster view: the track list aggregates every managed artist. */
  viewAllArtists: boolean;
}

const AudiolabContext = createContext<AudiolabContextType | undefined>(
  undefined,
);
export default AudiolabContext;
